import { useEffect, useState } from "react";
import {
  getSmartTriggerSettings,
  saveSmartTriggerEnabled,
  saveTriggerRequest,
  TriggerRequest,
} from "../../../api/leads.service";
import { businessId } from "../inbox/businessSetup";

const SWITCH_KEY = "fd24.smartTriggerHourly.v1";

function loadHourlyOn(): boolean {
  try {
    const all = JSON.parse(localStorage.getItem(SWITCH_KEY) || "{}");
    return all[businessId()] !== false;
  } catch {
    return true;
  }
}

function saveHourlyOn(enabled: boolean) {
  const all = JSON.parse(localStorage.getItem(SWITCH_KEY) || "{}");
  all[businessId()] = enabled;
  localStorage.setItem(SWITCH_KEY, JSON.stringify(all));
}

export default function SmartTriggersPage() {
  const [query, setQuery] = useState("");
  const [hourlyOn, setHourlyOn] = useState(loadHourlyOn);
  const [requests, setRequests] = useState<TriggerRequest[]>([]);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [detail, setDetail] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getSmartTriggerSettings(businessId())
      .then((settings) => {
        if (cancelled || !settings) return;
        saveHourlyOn(settings.enabled);
        setHourlyOn(settings.enabled);
        setRequests(settings.requests);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleHourly = async () => {
    const next = !hourlyOn;
    setHourlyOn(next);
    saveHourlyOn(next);
    setError("");
    try {
      await saveSmartTriggerEnabled(businessId(), next);
    } catch {
      setError("Could not save the switch. The hourly job keeps running until this is saved.");
    }
  };

  const sendRequest = async () => {
    const trimmedName = name.trim();
    const trimmedDetail = detail.trim();
    if (!trimmedName || !trimmedDetail) return;
    setSending(true);
    setError("");
    try {
      const saved = await saveTriggerRequest(businessId(), { name: trimmedName, text: trimmedDetail });
      setRequests((current) => current.concat(saved));
      setName("");
      setDetail("");
      setOpen(false);
    } catch {
      setError("Could not send the request. Try again.");
    } finally {
      setSending(false);
    }
  };

  const needle = query.trim().toLowerCase();
  const hourlyVisible = "every hour add or remove tags and move stages based on messages".includes(needle) || needle === "";
  const visibleRequests = requests.filter((request) => {
    const haystack = `${request.name} ${request.text}`.toLowerCase();
    return haystack.includes(needle);
  });

  return (
    <div className="mx-auto max-w-6xl py-4">
      <div className="mb-4 flex items-center gap-3">
        <h2 className="text-2xl font-semibold text-gray-900">Smart Triggers</h2>
        <button
          type="button"
          onClick={() => {
            setError("");
            setOpen(true);
          }}
          className="ml-auto rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white"
        >
          Request a trigger
        </button>
      </div>

      <input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search for any rule"
        className="mb-4 w-full rounded-lg border border-gray-200 px-4 py-2.5 text-sm"
      />

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
        <div className="grid grid-cols-[1.1fr_1.6fr_1.3fr_auto] bg-gray-50 px-4 py-3 text-sm text-gray-500">
          <span>Rule Name</span>
          <span>Trigger</span>
          <span>Action</span>
          <span />
        </div>
        {hourlyVisible ? (
          <div className="grid grid-cols-[1.1fr_1.6fr_1.3fr_auto] items-center gap-3 border-t border-gray-100 px-4 py-4 text-sm">
            <span className="truncate font-medium text-gray-800">Every hour</span>
            <span className="truncate text-gray-600">Every 1 hour, for each lead</span>
            <span className="truncate text-gray-600">Add or remove tags and move stages based on messages</span>
            <button
              type="button"
              role="switch"
              aria-checked={hourlyOn}
              aria-label="Hourly smart trigger"
              onClick={toggleHourly}
              className={`relative h-6 w-11 shrink-0 rounded-full ${hourlyOn ? "bg-blue-600" : "bg-gray-300"}`}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white ${hourlyOn ? "left-5" : "left-0.5"}`} />
            </button>
          </div>
        ) : null}
        {visibleRequests.map((request) => (
          <div key={request.id} className="grid grid-cols-[1.1fr_1.6fr_1.3fr_auto] items-center gap-3 border-t border-gray-100 px-4 py-4 text-sm">
            <span className="truncate font-medium text-gray-800" title={request.name}>{request.name}</span>
            <span className="truncate text-gray-600" title={request.text}>{request.text}</span>
            <span className="truncate text-gray-600">Waiting for us to create and approve</span>
            <span className="text-xs text-gray-400">Requested</span>
          </div>
        ))}
        {!hourlyVisible && visibleRequests.length === 0 ? (
          <p className="px-4 py-8 text-sm text-gray-500">No rules match this search.</p>
        ) : null}
      </div>
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}

      {open ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900">Request a trigger</h3>
            <p className="mt-1 text-sm text-gray-500">
              The hourly trigger is the same for every business. Describe what you need. We create it and approve it.
            </p>
            <label className="mt-4 block text-sm text-gray-600">
              Trigger name
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
            <label className="mt-3 block text-sm text-gray-600">
              What should this trigger do?
              <textarea
                value={detail}
                onChange={(event) => setDetail(event.target.value)}
                rows={4}
                className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm"
              />
            </label>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setOpen(false)} className="rounded-lg px-4 py-2 text-sm text-gray-600">
                Cancel
              </button>
              <button
                type="button"
                onClick={sendRequest}
                disabled={sending || !name.trim() || !detail.trim()}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:bg-gray-200"
              >
                Send request
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
